import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRelative } from "@/lib/utils";
import { useMdfComments } from "./use-mdf-comments";
import { useAddMdfComment } from "./use-add-mdf-comment";

// Partner-visible comment thread on a submitted MDF request. Reads via
// useMdfComments (live cache; the optimistic-append in use-add-mdf-comment
// keys off the same `requestComments(id)` query — so a new partner comment
// appears instantly and rolls back on error). Internal comments authored
// by Bookboost (via bucket 10's POST handler) surface here as well.

type Props = {
  requestId: string;
  canPost: boolean;
};

export function MdfCommentThread({ requestId, canPost }: Props) {
  const { data, isLoading, isError } = useMdfComments(requestId);
  const add = useAddMdfComment();
  const [body, setBody] = useState("");

  function handleSend() {
    const trimmed = body.trim();
    if (!trimmed || trimmed.length === 0) return;
    add.mutate(
      { requestId, body: trimmed },
      { onSuccess: () => setBody("") },
    );
  }

  return (
    <section>
      <h3 className="text-sm font-medium text-foreground">Comments</h3>

      {isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      ) : isError ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Couldn&rsquo;t load comments. Refresh to try again.
        </p>
      ) : (data ?? []).length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          No comments yet. Use the box below to leave a note for the
          Bookboost team.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {(data ?? []).map((c) => {
            const isPartner = c.author_role === "partner";
            return (
              <li
                key={c.id}
                className={
                  isPartner
                    ? "rounded-md border bg-card p-3"
                    : "rounded-md border border-accent/30 bg-accent/5 p-3"
                }
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {isPartner ? "You" : "Bookboost team"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatRelative(c.created_at)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                  {c.body}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {canPost ? (
        <div className="mt-4">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add context or ask a question…"
            rows={3}
            disabled={add.isPending}
          />
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleSend}
              disabled={add.isPending || body.trim().length === 0}
            >
              {add.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
